import { ColDef, ICellRendererParams } from "ag-grid-community";
import { SidePanelTableDataComponent } from "./side-panel-table-data.component";
import { CellLinkComponent } from "src/app/common/components/core/table/cell-link.component";
import { AppConstants } from "src/app/common/constants/app-constants";

export const columnDefinitionSidePanelTableData = (
  component: SidePanelTableDataComponent
): ColDef[] => {
  // Default cell renderer function
  const defaultCellRenderer = (params: ICellRendererParams) => {
    const value = params.value;
    if (value === null || value === undefined || value === AppConstants.emptyString) {
      return AppConstants.emDash;
    }
    return `<span title="${value}"> ${value} <span>`;
  };
  // Plain-language explanations for the Time Card Import Failures reason labels,
  // surfaced as hover tooltips. Keys must match the labels produced by the API.
  const reasonTooltips: Record<string, string> = {
    "Failed Time Clock Import":
      "A time clock punch for this person came in from the payroll system but could not be saved in RehabConnect.",
    "Employee ID Mismatch":
      "The Employee # on the incoming punch does not match the PBJ Employee ID of any active user at this facility.",
    "Missing Employee ID":
      "This active user has no PBJ Employee ID on file, so their time clock punches cannot be matched or imported.",
  };
  const caseInsensitiveComparator = (valueA: string, valueB: string): number => {
    const a = (valueA ?? AppConstants.emptyString).trim().toLowerCase();
    const b = (valueB ?? AppConstants.emptyString).trim().toLowerCase();
  
    if (a === AppConstants.emptyString && b === AppConstants.emptyString) return 0;
    if (a === AppConstants.emptyString) return -1;
    if (b === AppConstants.emptyString) return 1;
  
    return a.localeCompare(b);
  };
  
  return [
    // Patient Column (conditional)
    ...(component.headerDetails?.[1] ? [
      component.headerDetails[1].isRouteRequired
        ? {
            headerName: component.headerDetails?.[1].headerName,
            field: 'patientName',
            headerClass: "text-sm text-center",
            sortable: true,
            suppressMenu: true,
            cellRenderer: CellLinkComponent,
            cellRendererParams: { 
              clicked: component.navigate.bind(component) 
            },
            flex: 2,
            wrapText: true,
            autoHeight: true,
            comparator: caseInsensitiveComparator
          }
        : {
            headerName: component.headerDetails?.[1].headerName,
            field: 'patientName',
            headerClass: "text-sm text-center",
            sortable: true,
            suppressMenu: true,
            cellRenderer: defaultCellRenderer,
            flex: 2,
            wrapText: true,
            autoHeight: true,
            comparator: caseInsensitiveComparator
          }
    ] : []),

    // Therapist Column (conditional)
    ...(component.headerDetails?.[2] ? [
      component.headerDetails[2].isRouteRequired
        ? {
            headerName: component.headerDetails[2].headerName,
            field: 'therapistName',
            headerClass: "text-sm text-center",
            sortable: true,
            suppressMenu: true,
            cellRenderer: CellLinkComponent,
            cellRendererParams: {
              clicked: component.navigate.bind(component)
            },
            // Names stay on one line: wide enough for most full names, ellipsis +
            // hover tooltip for the rest.
            flex: 2.5,
            minWidth: 220,
            comparator: caseInsensitiveComparator,
            ...(component.headerDetails?.[8] ? { sort: "asc" as const, sortIndex: 1 } : {})
          }
        : {
            headerName: component.headerDetails[2].headerName,
            field: 'therapistName',
            headerClass: "text-sm text-center",
            sortable: true,
            suppressMenu: true,
            cellRenderer: defaultCellRenderer,
            flex: 2.5,
            minWidth: 220,
            comparator: caseInsensitiveComparator,
            ...(component.headerDetails?.[8] ? { sort: "asc" as const, sortIndex: 1 } : {})
          }
    ] : []),

    // Document Type Column (conditional)
    ...(component.headerDetails?.[3] ? [{
      headerName: component.headerDetails?.[3].headerName,
      field: 'documentType',
      headerClass: "text-sm text-center",
      cellClass: "text-center",
      sortable: true,
      suppressMenu: true,
      cellRenderer: defaultCellRenderer,
      flex: 1.5,
      wrapText: true,
      autoHeight: true,
      comparator: caseInsensitiveComparator
    }] : []),

    // Discipline Column (conditional)
    ...(component.headerDetails?.[4] ? [{
      headerName: component.headerDetails?.[4].headerName,
      field: 'discipline',
      headerClass: "text-sm text-center",
      cellClass: "text-center",
      sortable: true,
      suppressMenu: true,
      cellRenderer: defaultCellRenderer,
      flex: 1.5,
    }] : []),

    // Activity Date Column (conditional)
    ...(component.headerDetails?.[5] ? [{
      headerName: component.headerDetails?.[5].headerName,
      field: 'date',
      headerClass: "text-sm text-center",
      cellClass: "text-center",
      sortable: true,
      suppressMenu: true,
      cellRenderer: (params: ICellRendererParams) => {
        if(!!params?.value && params.value != "0001-01-01"){
          const[year, month, date] = params.value.split('-');
          const completeDate = month + "-" + date + "-" + year;
          return `<span title="${completeDate}"> ${completeDate} <span>`
        }
        return AppConstants.emDash
      },
      // Date is always a fixed-width MM-DD-YYYY; keep it narrow so wordier columns get the space.
      flex: 1,
      minWidth: 110,
      // On the import failures panel, default to newest first, then name A-Z
      // (dates are ISO yyyy-MM-dd strings, so the default comparator sorts correctly).
      ...(component.headerDetails?.[8] ? { sort: "desc" as const, sortIndex: 0 } : {})
    }] : []),

    // Error Reason Column (conditional)
    ...(component.headerDetails?.[8] ? [{
      headerName: component.headerDetails?.[8].headerName,
      field: 'errorReason',
      headerClass: "text-sm text-center",
      cellClass: "text-center",
      sortable: true,
      suppressMenu: true,
      cellRenderer: (params: ICellRendererParams) => {
        const value = params.value;
        if (value === null || value === undefined || value === AppConstants.emptyString) {
          return AppConstants.emDash;
        }
        return `<span title="${reasonTooltips[value] ?? value}"> ${value} </span>`;
      },
      flex: 2,
      // Wide enough for the longest reason label ("Failed Time Clock Import") on one line.
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      comparator: caseInsensitiveComparator
    }] : []),

    // Occurrence Count Column (conditional)
    ...(component.headerDetails?.[9] ? [{
      headerName: component.headerDetails?.[9].headerName,
      field: 'occurrenceCount',
      headerClass: "text-sm text-center",
      cellClass: "text-center",
      sortable: true,
      suppressMenu: true,
      cellRenderer: defaultCellRenderer,
      flex: 1,
    }] : []),
  ].filter(col => col !== undefined && col !== null);
};